import { env } from '../utils/env'
import { CognitoAuthService } from './cognitoAuth'

export interface ClarityConversationMessage {
  role: 'user' | 'assistant'
  content: string
}

export const clarityService = {
  async streamChat(
    prompt: string,
    messages: ClarityConversationMessage[],
    onToken: (token: string) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const token = await CognitoAuthService.validateAndRefreshToken()

    // Trailing slash required: no-slash historically 307'd to the ALB origin host
    // (uatapi...) and dropped Authorization. Dual BE routes still prefer the slash form.
    const response = await fetch(
      `${env.API_URL}/${env.API_VERSION}/stream/clarity/`,
      {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ prompt, messages }),
        signal,
      }
    )

    if (!response.ok) {
      throw new Error(`Stream request failed: ${response.status}`)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)
        if (data === '[DONE]') return

        let parsed: { token?: string; error?: string } | undefined
        try {
          parsed = JSON.parse(data)
        } catch {
          continue // ignore malformed SSE lines
        }

        if (parsed?.error) throw new Error(parsed.error)
        if (parsed?.token) onToken(parsed.token)
      }
    }
  },
}
